import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import LayoutHelper from 'infrastructure/LayoutHelper';
import { withFormik, FormikProps } from 'formik';
import {
  TextField,
  Paper,
  Button,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Typography,
  Divider,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import Error from '@material-ui/icons/Error';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { BookingTermsAndConditions } from './BookingTermsAndConditions';
import { Recaptcha } from '../../components/Shared/Recaptcha';
import MuiPhoneInput from '../../components/Form/MuiPhoneInput';
import InfoAlert from '../../components/Alerts/InfoAlert';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import UserHelper from '../../infrastructure/UserHelper';
import { Domain, Studio, StudioClient, User } from '../../types';
import { PendingClient } from '../Notifications/types';

interface FormValues {
  phone: string;
  clientName?: string;
  clientEmail?: string;
  clientNote?: string;
  pin?: string;
  studioClient?: StudioClient | null;
  pendingClient?: PendingClient | null;
  canSubmit?: boolean;
  tcAccepted?: boolean;
}

interface BookingFormState {
  validatingPin?: boolean;
  pinValid?: boolean;
  generatingPin?: boolean;
  showInfoMessage?: boolean;
  showPin?: boolean;
  validatingPhone?: boolean;
  isNew?: boolean;
  isPhoneValid?: boolean;
}

interface BookingFormFieldProps {
  label: string;
  name: string;
  children: React.ReactNode;
}

interface BookingFormOwnProps {
  studio: Studio;
  buttonTitle: string;
  owner: User;
  onSubmit: (values: FormValues) => Promise<void>;
  onSave: () => void;
  classes: any;
  errors: any;
}

interface BookingFormStateProps {
  translate: (key: string) => string;
  user: any; // Define a more specific user interface if possible
  myStudios: any; // Define a more specific myStudios interface if possible
  domain: Domain;
}

interface BookingFormProps
  extends BookingFormOwnProps,
    BookingFormStateProps,
    FormikProps<FormValues>,
    WithStyles<typeof styles> {}

class BookingForm extends React.Component<BookingFormProps, BookingFormState> {
  constructor(props: BookingFormProps) {
    super(props);
    this.state = {};
  }

  componentDidMount(): void {
    if (this.props.values.phone) {
      const phone = this.props.values.phone;
      this.getClient(phone);
    }
  }

  BookingFormField = ({ label, name, children }: BookingFormFieldProps): JSX.Element => {
    const { classes, errors } = this.props;
    const error = errors[name];
    return (
      <FormControl className={classes.field} error={!!error}>
        <InputLabel className={classes.label}>
          {label}
          {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
        </InputLabel>
        {children}
      </FormControl>
    );
  };

  onPinChanged = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const pin = e.target.value;
    this.props.handleChange('pin')(pin);
    if (!pin || pin.length != 4 || isNaN(parseInt(pin))) {
      return;
    }
    this.setState({ validatingPin: true });
    if (this.props.values.studioClient) {
      const api = serviceLocator.clientApi;
      api
        .checkPhonePin(this.props.studio.owner_id, this.props.values.phone, pin)
        .then((studioClientData: { client: StudioClient }) => {
          const studioClient = studioClientData.client;
          if (studioClient) {
            localStorage.setItem('validPhone', studioClient.phone);
            this.setState({ pinValid: true, validatingPin: false });
          } else {
            this.setState({ pinValid: false, validatingPin: false });
          }
        }, LayoutHelper.onErr);
    } else if (this.props.values.pendingClient) {
      const api = serviceLocator.pendingClientApi;
      api
        .validatePin(this.props.studio.owner_id, this.props.values.pendingClient._id, this.props.values.phone, pin)
        .then((studioClientData: { client: StudioClient }) => {
          const studioClient = studioClientData.client;
          if (studioClient) {
            localStorage.setItem('validPhone', studioClient.phone);
            this.setState({ pinValid: true, validatingPin: false });
          } else {
            this.setState({ pinValid: false, validatingPin: false });
          }
        }, LayoutHelper.onErr);
    }
  };

  generatePin = (): void => {
    this.setState({ generatingPin: true, showInfoMessage: true });

    if (this.props.values.studioClient) {
      const api = serviceLocator.clientApi;
      api.generatePin(this.props.studio.owner_id, this.props.values.studioClient._id).then(() => {
        this.setState({ generatingPin: false });
      });
    } else {
      const api = serviceLocator.pendingClientApi;
      api.generatePin(this.props.studio.owner_id, this.props.values.phone).then((pendingClient: PendingClient) => {
        this.props.handleChange('pendingClient')(pendingClient);
        this.setState({ generatingPin: false });
      });
    }
  };

  getClient = (phone: string): void => {
    this.setState({ showPin: false });
    if (!(phone && isValidPhoneNumber(phone))) return;
    this.setState({ validatingPhone: true });
    const api = serviceLocator.clientApi;
    api.checkPhoneOnSignUp(this.props.studio.owner_id, phone).then((data: { clients?: StudioClient[] }) => {
      let studioClient: StudioClient | undefined;
      if (data && data.clients && data.clients.length > 0) {
        studioClient = data.clients[0];
        this.props.handleChange('clientName')(studioClient.name);
        this.props.handleChange('clientEmail')(studioClient.email);
      } else {
        this.generatePin();
      }
      const isNew = studioClient && !studioClient.deleted ? false : true;
      this.props.handleChange('studioClient')(studioClient || null);
      this.setState({
        isNew,
        showPin: true,
        isPhoneValid: true,
        validatingPhone: false,
        pinValid: !this.props.studio.bookClassInfoWithPin,
      });
    });
  };

  render(): JSX.Element {
    const { BookingFormField } = this;
    const { classes, values, isSubmitting, handleChange, handleSubmit, domain, dirty } = this.props;
    return (
      <Paper className={classes.root} elevation={1}>
        <Typography align="center" variant="h6">
          {this.props.translate('myClients.clientInfo')}
        </Typography>
        <Divider />
        <form onSubmit={handleSubmit}>
          <BookingFormField label={this.props.translate('manageRegistration.clientPhone')} name="phone">
            <MuiPhoneInput
              defaultCountry={domain.countryCode}
              value={values.phone}
              id="phone"
              autoFocus
              onChange={(phone: string) => {
                handleChange('phone')(phone);
                handleChange('studioClient')(null);
                handleChange('pendingClient')(null);
                handleChange('clientEmail')(null);
                handleChange('clientName')(null);
                handleChange('canSubmit')(null);
                handleChange('tcAccepted')(null);
                handleChange('pin')(null);
                this.setState({ pinValid: !this.props.studio.bookClassInfoWithPin });
                return this.getClient(phone);
              }}
            />
          </BookingFormField>
          {this.state.validatingPhone && LayoutHelper.renderLoader()}
          {this.props.studio.bookClassInfoWithPin && this.state.showPin && (
            <BookingFormField label={this.props.translate('manageRegistration.pin')} name="pin">
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={this.state.pinValid || this.state.validatingPin}
                    autoComplete="off"
                    autoFocus
                    variant="outlined"
                    error={values.pin && !this.state.pinValid && this.props.translate('manageRegistration.invalidPin')}
                    inputProps={{ autofill: 'off' }}
                    id="pin"
                    value={values.pin}
                    onChange={this.onPinChanged}
                  />
                  {this.renderPinStatus()}
                </Grid>
                {!this.state.pinValid && (
                  <Grid item xs={12} md={6}>
                    <Button
                      disabled={this.state.generatingPin}
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={this.generatePin}
                    >
                      {this.state.generatingPin ? LayoutHelper.renderLoader() : ''}
                      {this.props.values.studioClient
                        ? this.props.translate('manageRegistration.remindPin')
                        : this.props.translate('manageRegistration.sendMePinAgain')}
                    </Button>
                  </Grid>
                )}
                {this.state.showInfoMessage && (
                  <Grid item xs={12}>
                    <InfoAlert infoText={this.props.translate('manageRegistration.savePinInfo')} />
                  </Grid>
                )}
              </Grid>
            </BookingFormField>
          )}
          {(!this.props.studio.bookClassInfoWithPin || this.state.pinValid) && (
            <Fragment>
              <BookingFormField label={this.props.translate('manageRegistration.clientName')} name="clientName">
                <TextField
                  autoFocus={Boolean(this.props.values.pendingClient)}
                  disabled={Boolean(this.props.values.studioClient)}
                  id="clientName"
                  value={values.clientName}
                  onChange={handleChange}
                  margin="normal"
                />
              </BookingFormField>
              <BookingFormField label={this.props.translate('manageRegistration.clientEmail')} name="clientEmail">
                <TextField
                  id="clientEmail"
                  disabled={Boolean(this.props.values.studioClient)}
                  value={values.clientEmail}
                  onChange={this.handleEmailChange}
                  margin="normal"
                />
              </BookingFormField>
              <BookingFormField label={this.props.translate('manageRegistration.clientNote')} name="clientNote">
                <TextField
                  id="clientNote"
                  multiline
                  value={values.clientNote}
                  onChange={handleChange}
                  margin="normal"
                />
              </BookingFormField>
              {this.state.isNew && (
                <Fragment>
                  {this.renderTC()}
                  <BookingFormField name="canSubmit" label={this.props.translate('common.captcha')}>
                    <div style={{ marginBottom: '10px' }}>
                      <Recaptcha onVerify={(canSubmit: boolean) => this.props.handleChange('canSubmit')(canSubmit)} />
                    </div>
                  </BookingFormField>
                </Fragment>
              )}
            </Fragment>
          )}
          <div className={classes.buttons}>
            <Button
              type="submit"
              size="large"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting || !this.state.pinValid || !dirty}
            >
              {isSubmitting && LayoutHelper.renderLoader()}
              {this.props.buttonTitle}
            </Button>
          </div>
        </form>
      </Paper>
    );
  }

  renderPinStatus = (): JSX.Element | undefined => {
    if (this.state.validatingPin) return LayoutHelper.renderLoader();
    if (this.state.pinValid) {
      return <CheckIcon />;
    } else {
      if (this.props.values.pin) {
        return <Error />;
      }
    }
    return undefined;
  };

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value;
    if (value) {
      value = value.trim();
    }
    this.props.handleChange('clientEmail')(value);
  };

  renderTC = (): JSX.Element => {
    const { BookingFormField } = this;
    const { values, handleChange, owner } = this.props;

    return (
      <BookingFormField label={this.props.translate('common.termsAndConditions')} name="tcAccepted">
        <BookingTermsAndConditions
          accepted={!!values.tcAccepted}
          onChange={(a: boolean) => {
            handleChange('tcAccepted')(a);
          }}
          owner={owner}
        />
      </BookingFormField>
    );
  };
}

function mapStateToProps(state: any): BookingFormStateProps {
  const translate = getTranslate(state.locale);
  const user = state.user;
  const myStudios = state.myStudios;
  const domain = state.domain.domain;
  return { translate, user, myStudios, domain };
}

const styles = (theme: any) => ({
  root: {
    marginBottom: `0 auto`,
    padding: theme.spacing(2),
  },
  buttons: {
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      left: 0,
      bottom: 0,
      width: '100%',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(2)}px 0`,
  },
  label: {
    marginBottom: theme.spacing(1),
    position: 'relative',
    transform: 'none',
    display: 'flex',
    alignItems: 'center',
  },
});

export default withStyles(styles)(
  connect(mapStateToProps)(
    withFormik<BookingFormOwnProps & BookingFormStateProps & WithStyles<typeof styles>, FormValues>({
      mapPropsToValues: (props) => {
        let phone = '';
        if (UserHelper.isLoggedIn(props.user)) {
          if (props.user.phone) {
            phone = props.user.phone;
          }
        } else {
          const storedPhone = localStorage.getItem('validPhone');
          if (storedPhone) {
            phone = storedPhone;
          }
        }

        return {
          phone,
        };
      },
      validate: (values, props) => {
        const errors: { [key: string]: string } = {};
        const mandatoryFields = ['clientName', 'phone'];
        if (values.pendingClient) {
          mandatoryFields.push('tcAccepted');
          mandatoryFields.push('canSubmit');
        }
        mandatoryFields.forEach((field) => {
          if (!values[field as keyof FormValues]) {
            errors[field] = '*';
          }
        });

        if (values.clientEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i.test(values.clientEmail)) {
          errors.clientEmail = props.translate('common.invalidEmail');
        }
        if (values.phone && !isValidPhoneNumber(values.phone)) {
          errors.phone = props.translate('common.invalidPhone');
        }
        return errors;
      },
      handleReset: (props) => {
        props.onSave();
      },
      handleSubmit: async (values, { setSubmitting, resetForm, props }) => {
        setSubmitting(true);
        await props.onSubmit(values);
        setSubmitting(false);
      },
      displayName: 'BookingForm',
    })(BookingForm)
  )
);
